import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  AccountBalanceWallet,
  Schedule,
  Description,
} from '@mui/icons-material';
import { ProductionAssumptionsData } from '../../types/widget';
import { apiService } from '../../services/api';

interface BudgetSummaryData {
  hasData: boolean;
  message?: string;
  currentDay?: number;
  originalBudget?: number;
  currentVariance?: number;
  estimatedFinalCost?: number;
  lastReportDay?: number;
  status?: string;
  variances?: Array<{
    department: string;
    varianceToDate: number;
    displayValue: string;
    displaySign: string;
  }>;
}

interface FinancialTrackingWidgetProps {
  widgetId: string;
  title: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatVariance = (variance: number, isCurrency: boolean = false): string => {
  const prefix = variance > 0 ? '+' : '';
  if (isCurrency) {
    return prefix + formatCurrency(variance);
  }
  return prefix + variance.toString();
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'over':
    case 'over_budget':
      return '#F56565';
    case 'under':
    case 'under_budget':
      return '#48BB78';
    case 'on_target':
    case 'on_budget':
    case 'tracking':
    case 'on_track':
      return '#4299E1';
    case 'reserved':
    case 'not_started':
    case 'baseline':
      return '#A0AEC0';
    default:
      return '#A0AEC0';
  }
};

const FinancialTrackingWidget: React.FC<FinancialTrackingWidgetProps> = ({ title }) => {
  const [assumptionsData, setAssumptionsData] = useState<ProductionAssumptionsData | null>(null);
  const [budgetData, setBudgetData] = useState<BudgetSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assumptionsResponse, budgetResponse] = await Promise.all([
        apiService.getProductionAssumptionsData(),
        apiService.getBudgetSummaryData(),
      ]);

      if (assumptionsResponse.success && assumptionsResponse.data) {
        setAssumptionsData(assumptionsResponse.data);
      }

      if (budgetResponse.success && budgetResponse.data) {
        setBudgetData(budgetResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!assumptionsData) {
    return <Alert severity="info">No financial data available.</Alert>;
  }

  const hasHotCostData = budgetData?.hasData;
  const hasOptionB = assumptionsData.totalBudget && assumptionsData.productionSpending;

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        backgroundColor: '#1A202C',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWallet sx={{ color: '#4299E1' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {assumptionsData.productionTitle} • {assumptionsData.baselineVersion}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchData} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {hasOptionB ? (
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            {/* Row 1: Total Budget & Production Spending */}
            {assumptionsData.totalBudget && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Budget
                    </Typography>
                    <Chip
                      label={assumptionsData.totalBudget.status === 'over' ? 'OVER COMMITTED' : 'ON TARGET'}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(assumptionsData.totalBudget.status),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Target</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(assumptionsData.totalBudget.target)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Committed</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(assumptionsData.totalBudget.committed)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: getStatusColor(assumptionsData.totalBudget.status),
                        fontWeight: 'bold',
                      }}
                    >
                      {formatVariance(assumptionsData.totalBudget.variance, true)}
                    </Typography>
                    {assumptionsData.totalBudget.variance > 0 && (
                      <Warning sx={{ color: '#F56565', fontSize: 18, ml: 0.5 }} />
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}

            {assumptionsData.productionSpending && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Production Budget
                    </Typography>
                    <Chip
                      label={hasHotCostData ? 'ACTIVE' : 'TRACKING'}
                      size="small"
                      sx={{
                        backgroundColor: hasHotCostData ? '#48BB78' : '#4299E1',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Budget</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(assumptionsData.productionSpending.budget)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Actuals</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(hasHotCostData ? (budgetData?.currentVariance || 0) + assumptionsData.productionSpending.budget : assumptionsData.productionSpending.actuals)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Day {assumptionsData.productionSpending.daysCompleted} of {assumptionsData.productionSpending.totalDays}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round((assumptionsData.productionSpending.daysCompleted / assumptionsData.productionSpending.totalDays) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(assumptionsData.productionSpending.daysCompleted / assumptionsData.productionSpending.totalDays) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#4A5568',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#4299E1',
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Row 2: Post-Production & Hot Cost Status */}
            {assumptionsData.postProduction && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Post-Production
                    </Typography>
                    <Chip
                      label="RESERVED"
                      size="small"
                      sx={{
                        backgroundColor: '#A0AEC0',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem',
                      }}
                    />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(assumptionsData.postProduction.budget)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Isolated from Hot Cost tracking
                  </Typography>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Hot Cost Status
                  </Typography>
                  {hasHotCostData ? (
                    <CheckCircle sx={{ color: '#48BB78', fontSize: 20 }} />
                  ) : (
                    <Schedule sx={{ color: '#A0AEC0', fontSize: 20 }} />
                  )}
                </Box>
                {hasHotCostData ? (
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: budgetData!.currentVariance! > 0 ? '#F56565' : '#48BB78' }}>
                      {formatVariance(budgetData!.currentVariance || 0, true)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cumulative variance through Day {budgetData!.lastReportDay}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="text.secondary">
                      Awaiting Hot Cost reports
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Upload Hot Cost to see actuals
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Row 3: Department Variances (when Hot Cost available) */}
            {hasHotCostData && budgetData?.variances && budgetData.variances.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Department Variances
                  </Typography>
                  <Grid container spacing={1}>
                    {budgetData.variances.slice(0, 6).map((v, idx) => (
                      <Grid item xs={6} md={4} key={idx}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, backgroundColor: '#1A202C', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {v.varianceToDate < 0 ? (
                              <TrendingDown sx={{ color: '#48BB78', fontSize: 16 }} />
                            ) : (
                              <TrendingUp sx={{ color: '#F56565', fontSize: 16 }} />
                            )}
                            <Typography variant="caption">{v.department}</Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold', color: v.varianceToDate < 0 ? '#48BB78' : '#F56565' }}
                          >
                            {v.displaySign}{v.displayValue}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Row 4: Script Baseline & Revision Comparison */}
            {assumptionsData.creative && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description sx={{ color: '#4299E1', fontSize: 20 }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Script Tracking
                      </Typography>
                    </Box>
                    {assumptionsData.scriptRevision?.hasRevision && (
                      <Chip
                        label="REVISED"
                        size="small"
                        sx={{
                          backgroundColor: '#ED8936',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.6rem',
                        }}
                      />
                    )}
                  </Box>
                  
                  {/* Baseline Row */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1.5, 
                    backgroundColor: '#1A202C', 
                    borderRadius: 1,
                    mb: 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label="BASELINE"
                        size="small"
                        sx={{
                          backgroundColor: '#4A5568',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.55rem',
                          height: 20,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Pages</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {assumptionsData.creative.originalPagesFormatted}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Scenes</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {assumptionsData.creative.originalScenes}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Days</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {assumptionsData.schedule?.original || 20}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>$/Page</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          ${assumptionsData.productionSpending?.costPerPage?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Current Revision Row (only show if there's a revision) */}
                  {/* Mirrors baseline layout exactly: Pages | Scenes | Days | $/Page */}
                  {assumptionsData.scriptRevision?.hasRevision && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 1.5, 
                      backgroundColor: '#2A4365', 
                      borderRadius: 1,
                      border: '1px solid #4299E1'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${assumptionsData.scriptRevision?.revisionColor?.toUpperCase() || 'CURRENT'} REV`}
                          size="small"
                          sx={{
                            backgroundColor: '#4299E1',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.55rem',
                            height: 20,
                          }}
                        />
                        {(assumptionsData.scriptRevision?.current?.omittedScenes ?? 0) > 0 && (
                          <Chip
                            label={`${assumptionsData.scriptRevision?.current?.omittedScenes} OMITTED`}
                            size="small"
                            sx={{
                              backgroundColor: '#ED8936',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.5rem',
                              height: 18,
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {/* Pages - mirrors baseline */}
                        <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Pages</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {assumptionsData.scriptRevision?.current?.pagesFormatted || assumptionsData.creative?.currentPagesFormatted}
                          </Typography>
                          {assumptionsData.scriptRevision?.variance?.pages !== 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: (assumptionsData.scriptRevision?.variance?.pages ?? 0) < 0 ? '#48BB78' : '#F56565',
                                fontWeight: 'bold',
                                fontSize: '0.6rem'
                              }}
                            >
                              {(assumptionsData.scriptRevision?.variance?.pages ?? 0) > 0 ? '+' : ''}
                              {assumptionsData.scriptRevision?.variance?.pages?.toFixed(1)}
                            </Typography>
                          )}
                        </Box>
                        {/* Scenes - mirrors baseline */}
                        <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Scenes</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {assumptionsData.scriptRevision?.current?.scenes || assumptionsData.creative?.currentScenes}
                          </Typography>
                          {assumptionsData.scriptRevision?.variance?.scenes !== 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: (assumptionsData.scriptRevision?.variance?.scenes ?? 0) < 0 ? '#F56565' : '#48BB78',
                                fontWeight: 'bold',
                                fontSize: '0.6rem'
                              }}
                            >
                              {(assumptionsData.scriptRevision?.variance?.scenes ?? 0) > 0 ? '+' : ''}
                              {assumptionsData.scriptRevision?.variance?.scenes}
                            </Typography>
                          )}
                        </Box>
                        {/* Days - mirrors baseline */}
                        <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Days</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {assumptionsData.scriptRevision?.current?.schedule || assumptionsData.schedule?.original || 20}
                          </Typography>
                        </Box>
                        {/* $/Page - mirrors baseline */}
                        <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>$/Page</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            ${assumptionsData.scriptRevision?.current?.costPerPage?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'}
                          </Typography>
                          {assumptionsData.scriptRevision?.variance?.costPerPageDelta !== 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: (assumptionsData.scriptRevision?.variance?.costPerPageDelta ?? 0) > 0 ? '#F56565' : '#48BB78',
                                fontWeight: 'bold',
                                fontSize: '0.6rem'
                              }}
                            >
                              {(assumptionsData.scriptRevision?.variance?.costPerPageDelta ?? 0) > 0 ? '+' : ''}
                              ${Math.abs(assumptionsData.scriptRevision?.variance?.costPerPageDelta || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Show simple baseline if no revision */}
                  {!assumptionsData.scriptRevision?.hasRevision && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Chip
                        label={`${assumptionsData.creative.scriptRevision} REV`}
                        size="small"
                        sx={{
                          backgroundColor: '#4299E1',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.6rem',
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : (
        /* Fallback: Legacy metrics display */
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="info">
            Financial data loading... Please refresh if this persists.
          </Alert>
        </Box>
      )}
    </Paper>
  );
};

export default FinancialTrackingWidget;
